import * as React from "react";
import { Artist } from "features/reviewable";
import styled from "@emotion/styled";
import { H3, Box, Button, FadeIn, Link, Text, FlexRow, Hr } from "atoms";
import { formatDate } from "utils/date";
import { useAuth } from "features/auth";
import { Post } from "../types";
import { useUpdatePost } from "../api/usePosts";

type Props = {
  artist: Artist;
  post: Post;
};

function PostAdmin({ artist, post }: Props) {
  const {
    roles: { showAdminFeatures },
  } = useAuth();
  const { mutate, isSuccess } = useUpdatePost(post.id);

  const showAdminEdit = post.automatedPost && showAdminFeatures;

  // Menus for Delete Are You Sure?
  const [showDeleteConfirmation, setShowDeleteConfirmation] =
    React.useState(false);
  const toggleDeleteConfirmation = () =>
    setShowDeleteConfirmation(!showDeleteConfirmation);

  const deletePost = () => {
    const deletedAt = new Date().toISOString();
    mutate({ ...post, deletedAt });
    setShowDeleteConfirmation(false);
  };

  return (
    <Wrapper>
      <H3 size="md">Manage Update</H3>
      {!post.deletedAt ? (
        <FlexRow flexDirection="column" gap="10px">
          {post.automatedPost ? (
            <Text size="xs">
              <em>Manifold posts cannot be edited</em>
            </Text>
          ) : (
            <Link
              variant="secondary"
              size="xs"
              to={`/artists/${artist.id}/updates/${post.id}/edit`}
            >
              Edit Update
            </Link>
          )}

          {showAdminEdit && (
            <Link
              variant="secondary"
              size="xs"
              to={`/artists/${artist.id}/updates/${post.id}/edit`}
            >
              Admin Edit Update
            </Link>
          )}

          {showDeleteConfirmation && !isSuccess ? (
            <FadeIn aria-live="polite">
              <Hr />
              <Text bold textAlign="center">
                Are you sure you want to delete this update?
              </Text>
              <FlexRow justifyContent="center" itemsFlex="0 0 auto" gap="10px">
                <Button variant="secondary" size="xs" onClick={deletePost}>
                  Delete
                </Button>
                <Button
                  variant="primary"
                  size="xs"
                  onClick={toggleDeleteConfirmation}
                >
                  Cancel
                </Button>
              </FlexRow>
            </FadeIn>
          ) : (
            <Button
              variant="secondary"
              size="xs"
              onClick={toggleDeleteConfirmation}
            >
              Delete Update
            </Button>
          )}
        </FlexRow>
      ) : (
        <Text style={{ margin: 0 }}>
          This update was deleted on{" "}
          <span style={{ whiteSpace: "nowrap" }}>
            {formatDate(post.deletedAt)}.
          </span>
        </Text>
      )}
    </Wrapper>
  );
}

const Wrapper = styled(Box)(() => ({
  gridArea: "admin",
  alignSelf: "self-start",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  position: "relative",
  overflow: "visible",
  zIndex: 2,
}));

const PostMenuClose = styled(Button)(({ theme }) => ({
  height: 30,
  width: 30,
  borderRadius: theme.borderRadius.sm,
  overflow: "hidden",
  position: "absolute",
  right: 0,
  top: 0,
  fontSize: 12,
  opacity: 0.4,
  "&:hover, &:focus": {
    border: 0,
    opacity: 1,
  },
}));
PostMenuClose.defaultProps = {
  variant: "blank",
};

export { PostAdmin };
